import type { KnockGuide, KnockGuideStep } from '@knocklabs/client'
import { KnockGuideContext, useGuides } from '@knocklabs/react-core'

import { Badge, type BadgeProps, Button, cn, TypographyDetail } from '@seen/ui'
import { IconInfoCircle, IconX } from '@tabler/icons-react'
import DOMPurify from 'dompurify'
import type { MouseEvent, ReactNode } from 'react'
import { useContext, useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router'
import { Dialog } from '#src/components/core/dialog/Dialog.tsx'
import { DialogClose } from '#src/components/core/dialog/DialogClose.tsx'
import { DialogContent } from '#src/components/core/dialog/DialogContent.tsx'
import { DialogDescription } from '#src/components/core/dialog/DialogDescription.tsx'
import { DialogFooter } from '#src/components/core/dialog/DialogFooter.tsx'
import { DialogHeader } from '#src/components/core/dialog/DialogHeader.tsx'
import { DialogTitle } from '#src/components/core/dialog/DialogTitle.tsx'
import { DialogDisplay } from '#src/components/core/dialog/display/DialogDisplay.tsx'
import { ScrollArea } from '#src/components/core/scroll-area/ScrollArea.tsx'
import {
  type GuideButton,
  type GuideImage as GuideImageContent,
  ORBIT_SIDEBAR_BANNER_GUIDE_TYPE,
  type OrbitSidebarBannerBadge,
  type OrbitSidebarBannerGuideContent,
  openOrbitSidebarBannerLink,
  parseOrbitSidebarBannerGuideContent,
} from '#src/components/knock/KnockOrbitSidebarBanner.utils.ts'

export interface BannerItem {
  content: OrbitSidebarBannerGuideContent
  guide: KnockGuide
  step: KnockGuideStep
}

export function KnockOrbitSidebarBanner(): ReactNode {
  const guideContext = useContext(KnockGuideContext)

  if (!guideContext) {
    return null
  }

  return <KnockOrbitSidebarBannerInner />
}

function KnockOrbitSidebarBannerInner(): ReactNode {
  const { guides } = useGuides({ type: ORBIT_SIDEBAR_BANNER_GUIDE_TYPE })
  const items = useMemo(
    () =>
      guides
        .map((guide) => {
          const step = guide.getStep()
          const content = parseOrbitSidebarBannerGuideContent(step?.content)

          if (!step || !content) {
            return undefined
          }

          return { content, guide, step }
        })
        .filter(isDefined),
    [guides]
  )

  if (items.length === 0) {
    return null
  }

  if (items.length === 1) {
    const { content, guide, step } = items[0]
    return (
      <KnockOrbitSidebarBannerItem
        key={guide.key}
        content={content}
        guide={guide}
        step={step}
      />
    )
  }

  return <KnockOrbitSidebarBannerStack items={items} />
}

// Approx card height including the inter-card gap, used only to cap the scroll
// viewport at MAX_CARDS so a partial card hints at more content below. Measured
// on a card that wraps to two lines of title and two lines of body.
const CARD_HEIGHT_WITH_GAP = 118
const MAX_CARDS = 3.5

export function KnockOrbitSidebarBannerStack({
  items,
}: {
  items: BannerItem[]
}): ReactNode {
  // Newest first, flat vertical list. pt-2 leaves top room so the topmost card's
  // protruding dismiss X isn't clipped.
  const cards = items.map(({ content, guide, step }) => (
    <KnockOrbitSidebarBannerItem
      key={guide.key}
      content={content}
      guide={guide}
      step={step}
    />
  ))

  // At or below the cap, render the cards directly at their natural height — no
  // scroll wrapper, no estimated row height, no reserved scrollbar lane.
  if (items.length <= MAX_CARDS) {
    return <div className="flex flex-col gap-2 pt-2">{cards}</div>
  }

  // Past the cap, a fixed (definite) height is required for the ScrollArea's
  // h-full viewport to resolve and scroll. The Root is widened 10px past the
  // slot so its right-anchored overlay scrollbar (w-2.5) lands in the slot's
  // right gutter; the matching pr-2.5 on the viewport content pulls the card
  // column back to the original slot width. Net: cards stay full-width, the bar
  // sits to their right in the gutter, and there is no visible reserved gap.
  const height = MAX_CARDS * CARD_HEIGHT_WITH_GAP + 8

  return (
    <div className="w-full" style={{ height: `${height}px` }}>
      <ScrollArea
        scrollRestorationId={null}
        type="always"
        className="h-full w-[calc(100%+10px)]"
        contentClassName="[&>div]:gap-2! [&>div]:pt-2! [&>div]:pr-2.5!"
      >
        {cards}
      </ScrollArea>
    </div>
  )
}

export function KnockOrbitSidebarBannerItem({
  content,
  guide,
  step,
  defaultOpen = false,
}: {
  content: OrbitSidebarBannerGuideContent
  guide: KnockGuide
  step: KnockGuideStep
  defaultOpen?: boolean
}): ReactNode {
  const [isOpen, setIsOpen] = useState(defaultOpen)
  const navigate = useNavigate()
  const hasVideo = content.image ? isVideoUrl(content.image.url) : false

  useEffect(() => {
    step.markAsSeen()
  }, [step])

  const openModal = (): void => {
    step.markAsInteracted({ metadata: { type: 'open_modal' } })
    setIsOpen(true)
  }

  const archive = (event?: MouseEvent): void => {
    event?.stopPropagation()
    step.markAsArchived()
    setIsOpen(false)
  }

  const closeModal = (): void => {
    setIsOpen(false)
  }

  const handleButtonClick =
    (name: 'primary_button' | 'secondary_button', button: GuideButton) =>
    (): void => {
      step.markAsInteracted({
        metadata: {
          type: 'button_click',
          name,
          text: button.text,
          href: button.href,
        },
      })

      if (button.href) {
        step.markAsArchived()
        closeModal()
        openOrbitSidebarBannerLink({
          href: button.href,
          navigate: (path) => {
            void navigate(path)
          },
        })
        return
      }

      step.markAsArchived()
      setIsOpen(false)
    }

  const handleContentClick = (event: MouseEvent<HTMLDivElement>): void => {
    const link =
      event.target instanceof Element ? event.target.closest('a') : null
    const href = link?.getAttribute('href')

    if (!href) {
      return
    }

    event.preventDefault()
    step.markAsArchived()
    closeModal()
    openOrbitSidebarBannerLink({
      href,
      navigate: (path) => {
        void navigate(path)
      },
    })
  }

  const sanitizedModalBody = sanitizeHtml(content.modalBody)
  const sanitizedBody = sanitizeBannerBodyHtml(content.body)

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <div
        className="group relative w-full rounded-md border bg-background text-left shadow-sm transition-colors hover:bg-frame-hover"
        data-guide-key={guide.key}
      >
        <Button
          type="button"
          variant="unstyled"
          size="unstyled"
          className="flex w-full min-w-0 flex-col items-stretch gap-1 overflow-hidden p-2.5 text-left whitespace-normal focus-visible:ring-2 focus-visible:ring-ring focus-visible:outline-hidden"
          onClick={openModal}
        >
          {(content.badge || content.emoji) && (
            <div className="flex max-w-full min-w-0 items-center gap-1.5">
              {content.emoji ? (
                <span className="shrink-0 text-sm leading-none" aria-hidden>
                  {content.emoji}
                </span>
              ) : (
                <IconInfoCircle className="size-3.5 shrink-0 text-muted-foreground" />
              )}
              {content.badge && (
                <Badge
                  className="min-w-0"
                  color={badgeColor(content.badge)}
                  variant="outline"
                >
                  {content.badge}
                </Badge>
              )}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <TypographyDetail className="line-clamp-2 leading-snug font-medium text-foreground">
              {content.title}
            </TypographyDetail>
            {sanitizedBody && (
              <div
                className="mt-0.5 line-clamp-2 text-xs leading-snug text-muted-foreground [&_p]:m-0"
                // oxlint-disable-next-line react/no-danger -- Knock guide HTML is sanitized with DOMPurify before rendering.
                dangerouslySetInnerHTML={{ __html: sanitizedBody }}
              />
            )}
          </div>
        </Button>
        {content.dismissible && (
          <Button
            type="button"
            size="icon-sm"
            variant="ghost"
            className="absolute -top-2 right-1 z-30 size-5 rounded-full border bg-background opacity-0 shadow-sm transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 hover:bg-frame-hover"
            onClick={archive}
            aria-label="Dismiss announcement"
          >
            <IconX className="size-3.5" />
          </Button>
        )}
      </div>

      <DialogDisplay scrollAreaContentClassName="max-h-[74dvh]">
        <DialogHeader className="pr-14">
          <DialogTitle className="flex items-center gap-2">
            {content.emoji && (
              <span className="shrink-0 text-xl leading-none" aria-hidden>
                {content.emoji}
              </span>
            )}
            <span>{content.modalTitle}</span>
          </DialogTitle>
        </DialogHeader>
        <DialogContent
          className={cn(
            'gap-4 py-0',
            hasVideo && 'sm:grid sm:grid-cols-[minmax(0,1fr)_auto] sm:gap-6'
          )}
        >
          <DialogDescription asChild>
            {/* oxlint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions -- delegated Markdown link clicks preserve native anchor keyboard behavior; the handler only intercepts clicks from sanitized Markdown anchors. */}
            <div
              className="max-w-none text-sm leading-6 text-foreground [&_a]:underline [&_a]:decoration-foreground-underline [&_a]:decoration-1 [&_a]:underline-offset-[2.5px] [&_a]:transition-colors hover:[&_a]:decoration-inherit [&_img]:my-3 [&_img]:w-full [&_img]:rounded-md [&_img]:border [&_li]:mb-1 [&_ol]:my-3 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-3 last:[&_p]:mb-0 [&_strong]:font-semibold [&_ul]:my-3 [&_ul]:list-disc [&_ul]:pl-5"
              onClick={handleContentClick}
              // oxlint-disable-next-line react/no-danger -- Knock guide HTML is sanitized with DOMPurify before rendering.
              dangerouslySetInnerHTML={{ __html: sanitizedModalBody }}
            />
          </DialogDescription>
          {content.image && <GuideMedia image={content.image} />}
        </DialogContent>
        <DialogFooter className="pt-4 sm:flex-row-reverse sm:justify-start">
          {content.primaryButton && (
            <Button
              onClick={handleButtonClick(
                'primary_button',
                content.primaryButton
              )}
            >
              {content.primaryButton.text}
            </Button>
          )}
          {content.secondaryButton && (
            <Button
              variant="outline"
              onClick={handleButtonClick(
                'secondary_button',
                content.secondaryButton
              )}
            >
              {content.secondaryButton.text}
            </Button>
          )}
          <DialogClose asChild>
            <Button variant={content.primaryButton ? 'outline' : 'default'}>
              Close
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogDisplay>
    </Dialog>
  )
}

function GuideMedia({ image }: { image: GuideImageContent }): ReactNode {
  const [hasError, setHasError] = useState(false)

  if (hasError) {
    return null
  }

  if (isVideoUrl(image.url)) {
    return (
      <video
        src={image.url}
        aria-label={image.alt}
        className="mx-auto max-h-[45dvh] max-w-full rounded-md border object-contain sm:mx-0"
        controls
        playsInline
        preload="auto"
        onError={() => setHasError(true)}
      />
    )
  }

  return (
    <img
      src={image.url}
      alt={image.alt}
      className="max-h-[55dvh] w-full rounded-md border object-contain"
      loading="lazy"
      onError={() => setHasError(true)}
    />
  )
}

function isVideoUrl(url: string): boolean {
  return /\.mp4(?:$|[?#])/i.test(url)
}

function sanitizeHtml(value: string | undefined): string {
  if (!value) {
    return ''
  }

  return DOMPurify.sanitize(value)
}

// The card itself is a button, so an anchor in the banner body would nest one
// interactive element inside another. Allowing only inline emphasis drops those
// tags while DOMPurify keeps their text.
function sanitizeBannerBodyHtml(value: string | undefined): string {
  if (!value) {
    return ''
  }

  return DOMPurify.sanitize(value, {
    ALLOWED_ATTR: [],
    ALLOWED_TAGS: ['b', 'br', 'code', 'em', 'i', 'p', 'strong'],
  })
}

function badgeColor(badge: OrbitSidebarBannerBadge): BadgeProps['color'] {
  switch (badge) {
    case 'Feature':
      return 'green'
    case 'Announcement':
      return 'blue'
    case 'Maintenance':
      return 'yellow'
  }
}

function isDefined<T>(value: T | undefined): value is T {
  return value !== undefined
}
